import { useMemo } from "react";

import type { SearchDialogSurface } from "commonComponents/utilities/MainNavigation/SearchDialog/searchDialog.learn";

import type { ISearchHit } from "./search.types";

interface ISearchFeedbackSubject {
    /**
     * Which surface the feedback was given from, by its name in search
     * telemetry so the two can be joined.
     */
    surface: SearchDialogSurface;
    searchId: string;
    traceId: string | null;
    /** What the user searched or asked. */
    query: string;
    results: readonly ISearchHit[] | null;
}

/**
 * The extra info a search surface sends with `AiSearch` feedback,
 * alike from each so the feedback reads the same wherever it came from. Keyed
 * `surface` like `AccountingSyncMoment`, the other target filed from more than
 * one place.
 *
 * A record without `surface` is from the retired nav dropdown. New `AiSearch`
 * feedback goes through this hook.
 */
export function useSearchFeedbackExtraInfo(subject: ISearchFeedbackSubject): string {
    const { surface, searchId, traceId, query, results } = subject;
    return useMemo(() => {
        const hits = results ?? [];
        return JSON.stringify({
            surface,
            searchId,
            traceId,
            query,
            resultCount: hits.length,
            topResults: hits.slice(0, 10).map((r) => ({
                entity: r.entity,
                id: r.id,
                summary: r.summary,
            })),
        });
    }, [surface, searchId, traceId, query, results]);
}
